<template>
  <validation-observer
    v-slot="subformObserver"
    slim
  >
    <form-section
      :id="`${sectionItemName}-section-start`"
      :validated="formObserver.validated"
      v-bind="subformObserver"
    >
      <slot
        name="question"
        :observer="subformObserver"
      />
      <form-row :is-vertical-splitted="false">
        <!-- Needed to track adding/removing items and track step changed state -->
        <validation-provider
          name="Items Quantity"
          immediate
        >
          <input
            type="hidden"
            :value="formItems"
          >
        </validation-provider>
        <validation-provider
          v-slot="{ invalid }"
          :vid="`${stepId}_${sectionItemName}`"
          :rules="{ is: { other: true } }"
          name="Items"
          immediate
        >
          <input
            type="hidden"
            :value="isAllItemsValid"
          >
          <div
            v-if="invalid"
            class="error--text mb-4 fz12"
          >
            Something doesn't look quite right here or there may be missing information. Please review details below:
          </div>
        </validation-provider>
      </form-row>
      <form-row
        v-if="isSubformVisible"
        :is-vertical-splitted="false"
        gap="48"
      >
        <template v-if="formItems.length > 0">
          <div
            v-for="(item, index) in formItemsWithId"
            :id="`${sectionItemName}-item-${index}`"
            :key="item.id"
            :class="{ 'mt-6': index !== 0 }"
          >
            <box
              v-if="!editableItems.includes(item.id)"
              :id="`item-${index}`"
              class="mb-8"
              :is-edit-btn-shown="false"
            >
              <slot
                name="preview"
                :item="{ item }"
              />

              <a
                slot="left-action"
                href="#"
                class="link-box"
                :data-test="`remove_${sectionItemName}_btn`"
                @click.prevent="removeItem(index)"
              >
                <span>
                  Remove
                </span>
                <v-icon
                  color="primary"
                  right
                >
                  mdi-delete
                </v-icon>
              </a>

              <a
                slot="right-action"
                href="#"
                class="link-box"
                :data-test="`change_${sectionItemName}_btn`"
                @click.prevent="changeItem(item.id)"
              >
                <span>Change these details</span>
                <v-icon color="primary">mdi-pencil</v-icon>
              </a>
            </box>

            <component
              :is="subform"
              v-else
              :key="item.id"
              is-edit
              :unique-id="item.id"
              :step-id="stepId"
              :item="item"
              :item-name="itemName"
              :questions="questions"
              :index="index + 1"
              :form-observer="formObserver"
              :claim-types-counters="claimTypesCounters"
              :is-additional="isAdditional"
              :section-item-name="sectionItemName"
              @save="updateItem($event, index)"
              @clear="cancelEditItem(item.id)"
            />
          </div>
        </template>

        <template v-if="newItem">
          <component
            :is="subform"
            :id="`${sectionItemName}-item-new`"
            :item="newItem"
            :questions="questions"
            :unique-id="sectionItemName"
            :item-name="itemName"
            :step-id="stepId"
            :step-index="stepIndex"
            :form-observer="formObserver"
            :claim-types-counters="claimTypesCounters"
            :is-additional="isAdditional"
            :class="{ 'mt-6': formItems.length > 0 }"
            :section-item-name="sectionItemName"
            @save="saveNewItem"
            @clear="resetItem"
          />
        </template>

        <div
          v-if="!newItem && !editableItems.length"
          class="mt-6"
        >
          <qz-btn
            v-if="formItems.length < maxAmount"
            color="navy-light"
            rounded
            x-large
            dark
            :data-test="`add_another_${sectionItemName}_btn`"
            @click.prevent="addItem"
          >
            Add
            {{ formItems.length > 0 ? 'another' : '' }}
            {{ itemName }}
            <v-icon
              dark
              right
              size="22"
            >
              mdi-plus
            </v-icon>
          </qz-btn>
          <p v-else>You can add up to {{ maxAmount }} {{ itemName }}s.</p>
        </div>
      </form-row>
    </form-section>
  </validation-observer>
</template>

<script>
import {
  uniqueId,
  cloneDeep,
} from 'lodash';
import { validate } from 'vee-validate';

export default {
  name: 'subform-controller',
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    showConfirmModal: {
      type: Function,
      required: true,
    },
    payloadTemplate: {
      type: Object,
      required: true,
    },
    subform: {
      type: Object,
      required: true,
    },
    isSubformVisible: {
      required: true,
      validator: value => [true, false, null].includes(value),
    },
    formItems: {
      type: Array,
      required: true,
    },
    maxAmount: {
      type: Number,
      required: true,
    },
    getRules: {
      type: Function,
      required: true,
    },
    itemName: {
      type: String,
      default: 'item',
    },
    stepId: {
      type: String,
      required: true,
    },
    stepIndex: {
      type: Number,
      default: 0,
    },
    sectionItemName: {
      type: String,
      required: true,
    },
    isAdditional: {
      type: Boolean,
      default: false,
    },
    questions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data () {
    return {
      editableItems: [],
      itemsValidity: null,

      newItem: null,

      itemsBackup: null,
    };
  },
  created () {
    this.$watch('isSubformVisible',
      async val => {
        if (val === true) {
          if (this.itemsBackup && this.itemsBackup.length > 0) {
            this.$emit('set-items', this.itemsBackup.slice());
            this.itemsBackup = null;
          } else if (this.formItems.length === 0 && !this.newItem) {
            this.newItem = cloneDeep(this.payloadTemplate);
            await this.$nextTick();
            this.$scrollTo(`#${this.sectionItemName}-item-new`);
          }
        }

        if (val === false) {
          if (this.formItems.length === 0) return;
          this.showConfirmModal({
            text: `Clicking Yes will remove these ${this.itemName}s.`,
            yesCallback: () => {
              this.$emit('set-question', false);
              this.itemsBackup = this.formItems.slice();
              this.$emit('set-items', []);
              this.$scrollTo(`#${this.sectionItemName}-section-start`);
            },
            noCallback: () => {
              this.$emit('set-question', true);
            },
          });
        }
      });

    this.$watch('formItems',
      async formItems => {
        if (!formItems || formItems.length === 0) {
          this.itemsValidity = null;
          return;
        }
        const results = await Promise.all(formItems.map(item => validate(item, {
          subform: {
            template: this.payloadTemplate,
            rules: this.getRules(item),
          },
        })));
        this.itemsValidity = results.slice();
      },
      {
        deep: true,
        immediate: true,
      });
  },
  computed: {
    isAllItemsValid () {
      return Array.isArray(this.itemsValidity) ? !this.itemsValidity.some(({ valid }) => !valid) : true;
    },
    formItemsWithId () {
      return this.formItems.map(item => {
        if (!item.id) {
          item.id = uniqueId();
        }

        return item;
      });
    },

    // claims type validation counter
    claimTypesCounters () {
      if (this.itemName !== 'claim') return null;

      if (!this.formItems || !this.formItems.length > 0) return null;

      // return obj with type of added claims as keys and counters of claims as values
      return this.formItems
        .filter(claim => !this.editableItems.includes(claim.id)) // don't count claims in edit mode
        .reduce((acc, claim) => {
          acc[claim.propertyClaimType] += 1;
          return acc;
        }, { B: 0, C: 0 });
    },
  },
  methods: {
    isItemValid (index) {
      return (this.itemsValidity && this.itemsValidity[index]) ? this.itemsValidity[index].valid : true;
    },
    async updateItem (updatedItem, index) {
      const items = this.formItems.map(a => ({ ...a }));
      const editableIndex = this.editableItems.indexOf(updatedItem.id);

      // Cleanup extra fields(<fieldName>Expired) but keep 'id' of the edited item
      items[index] = ['id', ...Object.keys(this.payloadTemplate)].reduce((acc, key) => {
        acc[key] = updatedItem[key];
        return acc;
      }, {});

      this.$emit('set-items', items);
      this.editableItems.splice(editableIndex, 1);
      this.$scrollTo(`#${this.sectionItemName}-section-start`);
    },
    changeItem (id) {
      this.editableItems.push(id);
    },
    cancelEditItem (id) {
      const index = this.editableItems.indexOf(id);
      this.editableItems.splice(index, 1);
    },
    async saveNewItem (item) {
      const items = this.formItems.map(a => ({ ...a }));
      items.push(item);
      this.$emit('set-items', items);

      await this.resetItem();
    },
    async resetItem () {
      this.newItem = null;
      await this.$nextTick();
      const itemsQty = this.formItems.length;
      if (itemsQty === 0) {
        this.$emit('set-question', false);
        this.$scrollTo(`#${this.sectionItemName}-section-start`);
      } else {
        this.$scrollTo(`#${this.sectionItemName}-item-${itemsQty - 1}`);
      }
    },
    async addItem () {
      this.newItem = cloneDeep(this.payloadTemplate);
      await this.$nextTick();
      this.$scrollTo(`#${this.sectionItemName}-item-new`);
    },
    removeItem (index) {
      this.showConfirmModal({
        noButton: 'Cancel',
        yesButton: 'Ok',
        yesCallback: () => {
          const items = this.formItems.map(a => ({ ...a })).slice();
          items.splice(index, 1);
          this.$emit('set-items', items);

          const itemsQty = items.length;

          if (itemsQty === 0) {
            this.$emit('set-question', false);
            this.$scrollTo(`#${this.sectionItemName}-section-start`);
          } else {
            this.$scrollTo(`#${this.sectionItemName}-item-${itemsQty - 1}`);
          }
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .box__actions {
    margin-left: 72px;
  }
}
</style>
